import { Distribution, DistributionParam } from "@custom-types/graphs"
import {
  AppNode,
  CategoricalNode,
  ContinuousNode,
  DeterministicNode,
  DiscreteNode,
  EdgeData,
  ForecastNode,
  ForecastTableNode,
  GroupNode,
  LagNode,
  MixtureNode,
  NodeType,
  ScenarioNode,
  ScenarioTableNode,
  TableNode,
  isNotGroupNode,
} from "@features/builder/graph/types"
import {
  continuousDistributionDefaultValues,
  doesNodeNeedTypeUpdate,
  getChildrenNodes,
  updateNodeType,
} from "@features/builder/helpers/nodes"
import { updateParentParamsOfChildNode } from "@features/builder/helpers/update-children-node"
import { distributionProperties } from "@utils/graph"
import { DfCpdRow, dfCpdToRows, rowsToDfCpd } from "@utils/node"
import { getParentsList } from "@utils/node/getParentsList"
import { Edge, ReactFlowInstance } from "@xyflow/react"
import { isEqual } from "lodash"

export const removeEdgeFromNode = <TChildNode extends AppNode>(
  parent: AppNode,
  child: TChildNode,
  allNodes: AppNode[],
): AppNode[] => {
  // group does not have real edges, do nothing
  if (!isNotGroupNode(child)) return [child]

  const parentNodeIds = getParentsList(child)
  if (!parentNodeIds.includes(parent.id)) return [child]

  const updatedParentNodeIds = parentNodeIds.filter((id) => id !== parent.id)

  const allNodesUpdatedMap = new Map(allNodes.map((node) => [node.id, node]))

  if (
    child.type === NodeType.Table ||
    child.type === NodeType.ForecastTable ||
    child.type === NodeType.ScenarioTable
  ) {
    const updatedChild = structuredClone(child)
    updatedChild.data.originalAttribute.properties.parents_lst = updatedParentNodeIds

    allNodesUpdatedMap.set(updatedChild.id, updatedChild)
  } else {
    let updatedChild = structuredClone(child)
    updatedChild.data.originalNode.properties.parents_lst = updatedParentNodeIds
    updatedChild = removeParentRelatedParameters(parent, updatedChild, allNodes)

    if (doesNodeNeedTypeUpdate(updatedChild, allNodes)) {
      updatedChild = updateNodeType(updatedChild, allNodes) as typeof updatedChild
    }

    allNodesUpdatedMap.set(updatedChild.id, updatedChild)

    const grandChildrenNodes = getChildrenNodes(
      updatedChild,
      Array.from(allNodesUpdatedMap.values()),
    )
    grandChildrenNodes.forEach((grandChild) => {
      const updatedNodes = updateParentParamsOfChildNode(
        grandChild,
        child,
        updatedChild,
        Array.from(allNodesUpdatedMap.values()),
      )
      updatedNodes.forEach((node) => allNodesUpdatedMap.set(node.id, node))
    })
  }

  const updatedNodes: AppNode[] = []

  for (const node of allNodes) {
    const updatedNode = allNodesUpdatedMap.get(node.id)
    const hasChanged = !isEqual(node, updatedNode)

    if (updatedNode && hasChanged) updatedNodes.push(updatedNode)
  }

  return updatedNodes
}

export const removeEdge = ({
  edgeId,
  reactFlowInstance,
}: {
  edgeId: string
  reactFlowInstance: ReactFlowInstance<AppNode, Edge<EdgeData>>
}) => {
  const { getEdge, getNode, getNodes, updateNode } = reactFlowInstance
  const edge = getEdge(edgeId)
  if (!edge || !edge.data?.finalSource || !edge.data?.finalTarget) return

  const targetNode = getNode(edge.data.finalTarget)
  const sourceNode = getNode(edge.data.finalSource)
  if (!sourceNode || !targetNode) return

  const updatedNodes = removeEdgeFromNode(sourceNode, targetNode, getNodes())

  updatedNodes.forEach((updatedNode) => {
    updateNode(updatedNode.id, updatedNode, { replace: true })
  })
}

const removeParentRelatedParameters = <TChildNode extends AppNode>(
  parent: AppNode,
  child: TChildNode,
  allNodes: AppNode[],
): TChildNode => {
  switch (child.type) {
    case NodeType.Continuous:
      return removeParentRelatedParametersFromContinuousChild(parent, child) as TChildNode
    case NodeType.Categorical:
      return removeParentRelatedParametersFromCategoricalChild(
        parent,
        child,
        allNodes,
      ) as TChildNode
    case NodeType.Lag:
      return removeParentRelatedParametersFromLagChild(parent, child, allNodes) as TChildNode
    case NodeType.Mixture:
      return removeParentRelatedParametersFromMixtureChild(parent, child, allNodes) as TChildNode
    case NodeType.Discrete:
      return removeParentRelatedParametersFromDiscreteChild(parent, child, allNodes) as TChildNode
    default:
      return child
  }
}

const removeParentRelatedParametersFromDiscreteChild = (
  parent: AppNode,
  child: DiscreteNode,
  allNodes: AppNode[],
): DiscreteNode => {
  switch (parent.type) {
    case NodeType.Discrete:
    case NodeType.Categorical:
      return removeParentRelatedParametersWithAllPosibleCombinationsFromChild(
        parent,
        child,
        allNodes,
      )
    default:
      return child
  }
}

const removeParentRelatedParametersFromMixtureChild = (
  parent: AppNode,
  child: MixtureNode,
  allNodes: AppNode[],
): MixtureNode => {
  switch (parent.type) {
    case NodeType.Categorical:
    case NodeType.Discrete:
      return removeParentRelatedParametersWithAllPosibleCombinationsFromChild(
        parent,
        child,
        allNodes,
      )
    case NodeType.Continuous:
    case NodeType.Mixture:
    case NodeType.Deterministic:
    case NodeType.Forecast:
    case NodeType.Scenario:
    case NodeType.Lag:
      return removeParentParameterFromChild(parent, child)
    default:
      return child
  }
}

const removeParentRelatedParametersFromLagChild = (
  parent: AppNode,
  child: LagNode,
  allNodes: AppNode[],
): LagNode => {
  switch (parent.type) {
    case NodeType.Lag:
      return removeLagParentRelatedParametersFromLagChild(parent, child, allNodes)
    default:
      return child
  }
}

const removeLagParentRelatedParametersFromLagChild = (
  parent: LagNode,
  child: LagNode,
  allNodes: AppNode[],
): LagNode => {
  const otherParentsIds = child.data.originalNode.properties.parents_lst
  const otherParents = otherParentsIds
    .map((id) => allNodes.find((node) => node.id === id))
    .filter((node) => node !== undefined)
  const doesStillHaveLagParent = otherParents.some((node) => node.type === NodeType.Lag)

  if (doesStillHaveLagParent) return child

  const updatedChild = structuredClone(child)
  updatedChild.data.originalNode.properties.df_cpd.expression = [""]
  updatedChild.data.originalNode.properties.a_sample_value = null

  return updatedChild
}

const removeParentRelatedParametersFromCategoricalChild = (
  parent: AppNode,
  child: CategoricalNode,
  allNodes: AppNode[],
): CategoricalNode => {
  switch (parent.type) {
    case NodeType.Categorical:
    case NodeType.Discrete:
      return removeParentRelatedParametersWithAllPosibleCombinationsFromChild(
        parent,
        child,
        allNodes,
      )
    default:
      return child
  }
}

// extend it to other parent node types or child node types if the logic is the same
const removeParentRelatedParametersWithAllPosibleCombinationsFromChild = <
  TChild extends CategoricalNode | MixtureNode | DiscreteNode,
>(
  parent: DiscreteNode | CategoricalNode,
  child: TChild,
  allNodes: AppNode[],
): TChild => {
  const currentRows = dfCpdToRows(child.data.originalNode.properties.df_cpd)

  const updatedRows: DfCpdRow[] = []
  // Only consider categorical/discrete parents when determining unique combinations.
  // Other parent types (continuous, mixture, etc.) are treated as coefficient parameters
  // and should not prevent deduplication when removing a categorical/discrete parent.
  const parentIds = getParentsList(child)
  const remainingsParentsIds = parentIds.filter((id) => {
    const node = allNodes.find((n) => n.id === id)
    return node?.type === NodeType.Categorical || node?.type === NodeType.Discrete
  })

  currentRows.forEach((row) => {
    const updatedRow = { ...row }
    delete updatedRow[parent.id]

    const updatedRowParentValues = remainingsParentsIds.map((id) => updatedRow[id])

    const combinationAlreadyCovered = updatedRows.some((r) => {
      const rowParentValues = remainingsParentsIds.map((id) => r[id])

      return isEqual(updatedRowParentValues, rowParentValues)
    })
    if (combinationAlreadyCovered) return

    updatedRows.push(updatedRow)
  })

  const updatedChild = structuredClone(child)
  updatedChild.data.originalNode.properties.df_cpd = rowsToDfCpd(updatedRows)

  return updatedChild
}

const removeParentRelatedParametersFromContinuousChild = (
  parent: AppNode,
  child: ContinuousNode,
): ContinuousNode => {
  switch (parent.type) {
    case NodeType.Continuous:
    case NodeType.Mixture:
    case NodeType.Deterministic:
    case NodeType.Forecast:
    case NodeType.Scenario:
    case NodeType.Lag:
      return removeParentParameterFromChild(parent, child)
    default:
      return child
  }
}

const removeParentParameterFromChild = <
  TChild extends Exclude<AppNode, GroupNode | TableNode | ForecastTableNode | ScenarioTableNode>,
>(
  parent: ContinuousNode | MixtureNode | DeterministicNode | ForecastNode | ScenarioNode | LagNode,
  child: TChild,
): TChild => {
  const updatedChild = structuredClone(child)
  delete updatedChild.data.originalNode.properties.df_cpd[parent.id]

  // replace the value of the distribution parameters that were linked to the parent node by a default value of that parameter
  if (updatedChild.type === NodeType.Continuous || updatedChild.type === NodeType.Mixture) {
    const rows = dfCpdToRows(updatedChild.data.originalNode.properties.df_cpd)

    const updatedRows: DfCpdRow[] = rows.map((row) => {
      const updatedRow = structuredClone(row)
      const distribution = row.distribution as Distribution
      const rowDistributionParams = distributionProperties[distribution]
      if (!rowDistributionParams) return updatedRow

      Object.entries(updatedRow).forEach(([key, value]) => {
        if (!rowDistributionParams.includes(key as DistributionParam)) return
        if (value !== parent.id) return

        updatedRow[key] =
          continuousDistributionDefaultValues?.[distribution]?.[key as DistributionParam] ?? null
      })

      return updatedRow
    })
    updatedChild.data.originalNode.properties.df_cpd = rowsToDfCpd(updatedRows)
  }

  return updatedChild
}
